import React from 'react';
import PropTypes from 'prop-types';

import {Integer, DecimalRounded, parseIntUtil} from '../../../utils/NumeralParts';
import LoanTermsLabelConverter from '../../../utils/LoanTermsLabelConverter';
import Formatters from '../../shared/utils/Formatters';

const propTypes = {
  termLength:             PropTypes.number.isRequired,
  repaymentFrequencyMode: PropTypes.string.isRequired,
  interestRate:           PropTypes.string.isRequired,
  serviceFee:             PropTypes.number.isRequired,
};

const pluralValue = (frequency, term) => {
  return frequency > 1 ? `${term}s` : term;
}

// NOTE: Might be a good idea to put this in a global place
const subInformationStyle = { color: '#414141' }

const renderAmortizingRepayment = ({
  goLiveDate,
  interestRate,
  repaymentAmount,
  repaymentAmountPer,
  repaymentDate,
  repaymentFrequency,
  repaymentFrequencyMode,
  serviceFee,
  paymentsCount
}) => {
  const termStart       = () => Formatters.date(goLiveDate);
  const termEnd         = () => Formatters.date(repaymentDate);

  const repaymentFrequencyValue = () => {
    return repaymentFrequency > 1 ? `${repaymentFrequency} ` : '';
  }

  const displayFrequencyLabel = () =>{
    const repaymentAmount = DecimalRounded({ number: repaymentAmountPer })
    const repaymentFrequency = repaymentFrequencyValue()
    const repaymentFrequencyModeLabel = pluralValue(repaymentFrequency, repaymentFrequencyMode)

    const content =
      repaymentFrequency === 0
        ? `${repaymentAmount} every ${repaymentFrequency} ${repaymentFrequencyModeLabel}`
        : `${repaymentAmount}`;

    return(
      <span
        className='-primary _alignSelf-center emphasize'
        data-testid='amortized-payment-decimal-frequency'
      >
        {content}
      </span>
    )
  }

  const displayFrequencyDescription = () => {
    const optionTermsLabel = LoanTermsLabelConverter({
      repaymentFrequency,
      repaymentFrequencyMode
    });

    const frequency = repaymentFrequencyMode === 'single_payment'
          ? optionTermsLabel
          : `${optionTermsLabel} (${paymentsCount} payments)`;

    return(
      <p className='_marginBottom--medium' style={subInformationStyle}>{frequency}</p>
    );
  }

  return (
    <section data-testid='amortizing-loan-repayment' className='cardSection'>
      <div className='subhead -primary'>
        You will pay
      </div>
      <div className='_displayFlex _justifyContent-center _alignItems-center'>
        <span className='-primary _marginRight--xsmall _marginBottom--small emphasize'>₱</span>
        <h2
          className='headline -primary _fontSize--xxlarge'
          data-testid='amortized-payment-whole-number'
        >
          {Integer(repaymentAmountPer)}
        </h2>
        {displayFrequencyLabel()}
      </div>
      {displayFrequencyDescription()}
      <div className='_displayFlex _justifyContent-space-evenly _marginBottom--small' style={subInformationStyle}>
        <div>
          <strong>Interest rate</strong>
          <p data-testid='repayment-interest-rate'>{`${Formatters.currencyDecimal(interestRate, false)}%/month`}</p>
        </div>
        <div>
          <strong>Total Payment</strong>
          <p data-testid='repayment-service-fee-amount'>
            {`₱ ${Formatters.currencyDecimal(repaymentAmount)}`}
          </p>
        </div>
      </div>
      <div className='_textAlign_center' style={subInformationStyle}>
        <strong>Loan Term <sup>1</sup></strong>
        <p data-testid='repayment-term-duration'>{`${termStart()} - ${termEnd()}`}</p>
      </div>
    </section>
  );
}

const renderAmortizingRepaymentForFlexible = ({loanOffer, paymentPlan}) => {
  const paymentSchedules = paymentPlan.payment_schedules;
  const firstSchedule = paymentSchedules[0];
  const lastSchedule = paymentSchedules[paymentSchedules.length - 1];
  const termStart       = () => Formatters.date(loanOffer.go_live_date);
  const termEnd         = () => Formatters.date(lastSchedule.due_date);
  const duration = (paymentPlan) => {
    let mode = paymentPlan.frequency_mode;
    let payment = 'payment'
    const paymentCount = paymentPlan.payment_schedules.length;
    if (paymentPlan.frequency_mode === 'week' && parseIntUtil(paymentPlan.frequency) === 2 ) { mode = 'two weeks' }
    if (paymentCount > 1) { payment = 'payments' }
    return `every ${mode} (${paymentCount} ${payment})`
  };

  return (
    <section data-testid='amortizing-loan-repayment-details' className='cardSection'>
      <p className='subhead -primary'>You will pay</p>
      <div className='_displayFlex _justifyContent-center _alignItems-center'>
        <small className='-primary _marginRight--xsmall _marginBottom--small'>₱</small>
        <h2 className='headline -primary _fontSize--xxlarge'>
          {Integer(firstSchedule.total)}
        </h2>
        <span className='-primary _alignSelf-center emphasize'>
          {`${DecimalRounded({number: firstSchedule.total})}`}
        </span>
      </div>
      <p className='_marginTop--none'>{duration(paymentPlan)}</p>
      <div className='_displayFlex _justifyContent-space-evenly _marginTop--medium'>
        <div className=''>
          <strong className='-base'>Interest rate</strong>
          <p className='-base'>{`${Formatters.currencyDecimal(paymentPlan.equivalent_aor)}%/mo.`}</p>
        </div>
        <div className=''>
          <strong className='-base'>Total payment</strong>
          <p className='-base'>{Formatters.currency({amount: paymentPlan.total, format: '0,0'})}</p>
        </div>
      </div>
      <div className='_textAlign_center _marginTop--medium'>
        <strong className='-base'>Loan Term</strong>
        <p className='-base' data-testid='loan-term'>{`${termStart()} - ${termEnd()}`}</p>
      </div>
    </section>
  )
}

const AmortizingRepayment = ({
  goLiveDate,
  interestRate,
  repaymentAmount,
  repaymentAmountPer,
  repaymentDate,
  repaymentFrequency,
  repaymentFrequencyMode,
  serviceFee,
  serviceFeeAmount,
  termLength,
  termLengthMode,
  loanOffer,
  paymentsCount,
  paymentPlan
}) => {
  const { amortising_payment_options = []}  = loanOffer;
  if (amortising_payment_options.length > 1) {
    return renderAmortizingRepaymentForFlexible({loanOffer, paymentPlan});
  }
  return renderAmortizingRepayment({
    goLiveDate,
    interestRate,
    repaymentAmount,
    repaymentAmountPer,
    repaymentDate,
    repaymentFrequency,
    repaymentFrequencyMode,
    serviceFee,
    serviceFeeAmount,
    termLength,
    termLengthMode,
    paymentsCount
  });
}

AmortizingRepayment.propTypes = propTypes;
export default AmortizingRepayment;



// WEBPACK FOOTER //
// ./src/features/loans/components/AmortizingRepayment.js